import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Button, Gallery, cn } from '@divlab/divanui';

import Link from '@Navigation/Link';
import Img from '@UI/Img';
import CardInView from '@Components/CardInView';
import getProductName from '@Utils/getProductName';
import PopupOnMap from '../PopupOnMap';
import MenuTabHead from '../MenuTabHead';
import WorkTimeNotification from './elements/WorkTimeNotification';
import styles from './PopupSelectedSellPoint.module.css';

import type { SellPointData } from '@Types/SellPoints';
import type { FC, HTMLAttributes, MouseEvent } from 'react';

export interface PopupSelectedSellPointProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  selectedPoint: SellPointData;
  onBack?: (e: MouseEvent) => void;
  onGoFull?: (e: MouseEvent, selectedPoint: SellPointData) => void;
  onChangeParams?: (params: string) => void;
}

const PopupSelectedSellPoint: FC<PopupSelectedSellPointProps> = (props) => {
  const { className, selectedPoint, onChangeParams, onBack, ...restProps } = props;
  const [isRouteOpen, setIsRouteOpen] = useState(false);
  const handleRoute = () => setIsRouteOpen(true);

  const ref = useRef<HTMLDivElement>();
  const [textHeightCheck, setTextHeightCheck] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (ref && ref.current && ref.current.clientHeight && ref.current.clientHeight >= 23) {
        setTextHeightCheck(true);
      }
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [selectedPoint]);

  const [selectedTabProducts, setSelectedTabProducts] = useState(
    selectedPoint?.products ? selectedPoint?.products.title : '',
  );
  const [selectedTabHowToGet, setSelectedTabHowToGet] = useState(
    selectedPoint?.howToGet ? selectedPoint?.howToGet.title : '',
  );

  const handleClickTab = useCallback(
    (e: MouseEvent, tab: string) => {
      setSelectedTabProducts(tab);
      setSelectedTabProducts(selectedTabProducts === tab ? null : tab);
    },
    [selectedTabProducts],
  );

  const handleClickTabHowToGet = useCallback(
    (e: MouseEvent, tab: string) => {
      setSelectedTabHowToGet(tab);
      setSelectedTabHowToGet(selectedTabHowToGet === tab ? null : tab);
    },
    [selectedTabHowToGet],
  );

  const hasNearestStation =
    selectedPoint.howToGet &&
    selectedPoint.howToGet?.nearestStation &&
    selectedPoint.howToGet?.nearestStation.length > 0;
  const hasProductsList =
    selectedPoint.products &&
    selectedPoint.products?.list &&
    selectedPoint.products?.list.length > 0;

  return (
    <>
      <PopupOnMap
        {...restProps}
        onChangeParams={onChangeParams}
        title={
          <div className={styles.top}>
            <div className={styles.back} onClick={onBack}>
              <div className={styles.arrowBack} />
            </div>
            <div
              className={styles.name}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: selectedPoint.name }}
            />
          </div>
        }
        cnScroller={styles.content}
        className={className}
      >
        <div className={styles.pointAddressWrapper}>
          <div
            className={styles.pointAddress}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: selectedPoint.address }}
          />
        </div>
        <div className={styles.underline}></div>
        <div className={styles.telephoneBlock}>
          <div className={styles.phoneIcon} />
          <Link
            className={styles.telephone}
            to={`tel:+${selectedPoint.phone.replace(/\D/g, '')}`}
            view='primary'
          >
            {selectedPoint.phone}
          </Link>
        </div>
        {selectedPoint.workTime.map((time, index) => {
          return (
            // eslint-disable-next-line react/no-array-index-key
            <div key={index} className={styles.workTimeBlock}>
              <div
                className={cn(styles.workTime, styles.muted)}
                dangerouslySetInnerHTML={{
                  __html: `${time[0]}`,
                }}
              />
              <div
                className={styles.workTime}
                dangerouslySetInnerHTML={{
                  __html: `${time[1]}`,
                }}
              />
            </div>
          );
        })}

        {selectedPoint.tableau?.length && (
          <WorkTimeNotification
            data={selectedPoint.tableau}
            className={styles.workTimeNotificationBlock}
          />
        )}

        {selectedPoint.howToGet && hasNearestStation && (
          <div className={styles.dropdown}>
            <MenuTabHead
              tab={selectedPoint.howToGet.title}
              onClick={handleClickTabHowToGet}
              selectedTab={selectedTabHowToGet}
            />

            <div
              className={styles.galleryWrapper}
              style={{
                display: selectedTabHowToGet === selectedPoint.howToGet.title ? 'block' : 'none',
              }}
            >
              <div className={styles.howToGetBlock}>
                {selectedPoint.howToGet.nearestStation.map((item, index) => {
                  const point = selectedPoint.howToGet.nearestStation;
                  const isLastItem = index === point.length - 1 && point.length > 1;
                  const needsMargin = isLastItem && textHeightCheck;

                  return (
                    <div
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      className={cn(styles.howToGet, needsMargin ? styles.howToGetTop : '')}
                    >
                      <Img src={item.transportIcon} className={styles.howToGetIcon} />
                      <div
                        ref={ref}
                        className={cn(styles.stationName, needsMargin ? styles.stationNameTop : '')}
                        dangerouslySetInnerHTML={{
                          __html: `${item.name}`,
                        }}
                      />
                      <div className={styles.distanceBlock}>
                        <Img src={item.pedestrianIcon} className={styles.pedestrianIcon} />
                        <div
                          className={styles.distance}
                          dangerouslySetInnerHTML={{
                            __html: `${item.distance}`,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {selectedPoint.products && hasProductsList && (
          <div className={styles.dropdown}>
            <MenuTabHead
              tab={selectedPoint.products.title}
              isProducts={selectedPoint.products.list.length !== 0}
              linkTo={selectedPoint.products.showAllProductsLink}
              seeAll={selectedPoint.products.showAllProductsText}
              onClick={handleClickTab}
              selectedTab={selectedTabProducts}
            />

            <div
              className={styles.product}
              style={{
                display: selectedTabProducts === selectedPoint.products.title ? 'block' : 'none',
              }}
            >
              <Gallery scrollbar spaceBetween={15}>
                {selectedPoint.products.list.map((product, index) => {
                  return (
                    <div
                      key={product.name}
                      data-testid='showroom-item'
                      className={styles.productWrapper}
                    >
                      <Link to={product.link}>
                        <CardInView
                          id={product.id}
                          listTitle='Шоурум'
                          listType='default'
                          position={index}
                          card={product}
                          cardType='product'
                        >
                          <Img
                            src={product.images[0]?.src}
                            className={styles.productImage}
                            cnImage={styles.cnImage}
                          />
                          <div className={styles.productTitle}>{getProductName(product)}</div>
                        </CardInView>
                      </Link>
                    </div>
                  );
                })}
              </Gallery>
            </div>
          </div>
        )}
      </PopupOnMap>

      <div className={styles.buttonWrapper}>
        {!isRouteOpen ? (
          <Button theme='primary' className={styles.routeButton} onClick={handleRoute}>
            {selectedPoint.route.buttonText}
          </Button>
        ) : (
          <div>
            <div
              className={styles.name}
              dangerouslySetInnerHTML={{
                __html: `${selectedPoint.route.title}`,
              }}
            />

            <div className={styles.routeButtons}>
              {selectedPoint.route.maps.map((map) => {
                return (
                  <Link key={map.text} to={map.href} className={styles.routeButton}>
                    <Button theme='secondary' className={styles.routeButton}>
                      {map.text}
                    </Button>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default memo(PopupSelectedSellPoint);
